import { useState, useMemo, useEffect } from 'react';
import { ModelInfo } from './types';
import { useI18n } from '../../i18n';

// Extract source prefix (e.g., "bltcy" from "bltcy/qwen3-max") and the bare model ID
function extractSourcePrefix(modelId: string): { source: string | null; bareId: string } {
  const slashIdx = modelId.indexOf('/');
  if (slashIdx > 0) {
    return { source: modelId.slice(0, slashIdx).toLowerCase(), bareId: modelId.slice(slashIdx + 1) };
  }
  return { source: null, bareId: modelId };
}

// Extract model family (e.g., "gpt", "claude", "deepseek", "doubao")
function extractModelFamily(modelId: string): string {
  // Strip source prefix before extracting family
  const { bareId } = extractSourcePrefix(modelId);
  const id = bareId.toLowerCase();

  // Extract first segment before version indicators
  const match = id.match(/^([a-z]+(?:-[a-z]+)?)/);
  if (match) {
    // Normalize common variations
    const family = match[1];
    if (family.startsWith('gpt')) return 'gpt';
    if (family.startsWith('claude')) return 'claude';
    if (family.startsWith('deepseek')) return 'deepseek';
    if (family.startsWith('doubao')) return 'doubao';
    if (family.startsWith('qwen')) return 'qwen';
    if (family.startsWith('gemini')) return 'gemini';
    if (family.startsWith('llama')) return 'llama';
    return family;
  }
  return id;
}

// Parse version from model ID for sorting
// Handles formats: gpt-5.2, gpt-4o-2024-05-13, claude-3-5-sonnet-20241022, deepseek-v3.2, etc.
function parseVersion(modelId: string): number[] {
  // Extract date patterns first (YYYY-MM-DD or YYYYMMDD)
  const dateMatch = modelId.match(/(\d{4})-?(\d{2})-?(\d{2})/);
  if (dateMatch) {
    return [parseInt(dateMatch[1]), parseInt(dateMatch[2]), parseInt(dateMatch[3])];
  }

  // Extract version numbers
  const versionParts: number[] = [];
  const parts = modelId.split(/[-_]/);
  for (const part of parts) {
    // Match "v3.2" style versions (with v prefix)
    const vPrefixMatch = part.match(/^v(\d+)(?:\.(\d+))?$/i);
    if (vPrefixMatch) {
      versionParts.push(parseInt(vPrefixMatch[1]));
      if (vPrefixMatch[2]) versionParts.push(parseInt(vPrefixMatch[2]));
      continue;
    }

    // Match decimal versions like "5.2" → [5, 2]
    const decimalMatch = part.match(/^(\d+)\.(\d+)$/);
    if (decimalMatch) {
      versionParts.push(parseInt(decimalMatch[1]), parseInt(decimalMatch[2]));
      continue;
    }

    // Match integers with optional "o" suffix (like gpt-4o)
    const numMatch = part.match(/^(\d+)(o)?$/);
    if (numMatch) {
      const base = parseInt(numMatch[1]);
      // "o" suffix gets a small boost to sort after base version
      versionParts.push(numMatch[2] ? base + 0.5 : base);
    }
  }
  return versionParts.length > 0 ? versionParts : [0];
}

// Compare two version arrays (descending order - newer first)
function compareVersions(a: number[], b: number[]): number {
  const maxLen = Math.max(a.length, b.length);
  for (let i = 0; i < maxLen; i++) {
    const aVal = a[i] ?? 0;
    const bVal = b[i] ?? 0;
    if (bVal !== aVal) return bVal - aVal; // Descending
  }
  return 0;
}

interface ModelSelectorModalProps {
  open: boolean;
  onClose: () => void;
  value: string | null;
  onChange: (value: string | null) => void;
  models: ModelInfo[];
  title: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
}

export function ModelSelectorModal({
  open,
  onClose,
  value,
  onChange,
  models,
  title,
  allowEmpty = false,
  emptyLabel = '-- None --',
}: ModelSelectorModalProps) {
  const { language } = useI18n();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(value);
  const [sourceFilter, setSourceFilter] = useState<string | null>(null); // null = default (no prefix)

  // Compute available source prefixes from models
  const sourcePrefixes = useMemo(() => {
    const prefixes = new Set<string>();
    for (const model of models) {
      const { source } = extractSourcePrefix(model.id);
      if (source) prefixes.add(source);
    }
    return Array.from(prefixes).sort();
  }, [models]);

  // Reset selected when modal opens
  useEffect(() => {
    if (open) {
      setSelected(value);
      setSearch('');
      // Auto-select source filter based on current value's prefix
      if (value) {
        const { source } = extractSourcePrefix(value);
        setSourceFilter(source);
      } else {
        setSourceFilter(null);
      }
    }
  }, [open, value]);

  // Group models by family
  const groupedModels = useMemo(() => {
    let result = models;

    // Filter by source prefix
    result = result.filter(m => {
      const { source } = extractSourcePrefix(m.id);
      return sourceFilter === null ? source === null : source === sourceFilter;
    });

    // Filter by search
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        m =>
          m.id.toLowerCase().includes(lower) ||
          m.display_name.toLowerCase().includes(lower) ||
          m.provider?.toLowerCase().includes(lower)
      );
    }

    // Group by family
    const groups: Record<string, ModelInfo[]> = {};
    for (const model of result) {
      const family = extractModelFamily(model.id);
      if (!groups[family]) {
        groups[family] = [];
      }
      groups[family].push(model);
    }

    // Sort models within each group by version descending, then alphabetically
    for (const family of Object.keys(groups)) {
      groups[family].sort((a, b) => {
        const versionA = parseVersion(a.id);
        const versionB = parseVersion(b.id);
        const versionCmp = compareVersions(versionA, versionB);
        if (versionCmp !== 0) return versionCmp;
        return a.id.localeCompare(b.id);
      });
    }

    // Sort families alphabetically and return as array of [family, models]
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [models, search, sourceFilter]);

  const handleConfirm = () => {
    onChange(selected);
    onClose();
  };

  // USD to RMB exchange rate (approximate)
  const USD_TO_RMB = 7.2;

  const formatPrice = (model: ModelInfo) => {
    if (!model.pricing) return null;
    const { input_per_million, output_per_million } = model.pricing;
    if (input_per_million === null && output_per_million === null) return null;

    if (language === 'zh') {
      // Show RMB for Chinese
      const inputRmb = input_per_million !== null ? (input_per_million * USD_TO_RMB).toFixed(2) : '?';
      const outputRmb = output_per_million !== null ? (output_per_million * USD_TO_RMB).toFixed(2) : '?';
      return `¥${inputRmb} 输入 / ¥${outputRmb} 输出`;
    }

    // Show USD for English
    return `$${input_per_million?.toFixed(2) ?? '?'} in / $${output_per_million?.toFixed(2) ?? '?'} out`;
  };

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black opacity-50 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal Content */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6 z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search models..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Source Filter Tabs */}
          {sourcePrefixes.length > 0 && (
            <div className="flex gap-1.5 mb-3">
              <button
                type="button"
                onClick={() => setSourceFilter(null)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  sourceFilter === null
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {language === 'zh' ? '默认' : 'Default'}
              </button>
              {sourcePrefixes.map(prefix => (
                <button
                  key={prefix}
                  type="button"
                  onClick={() => setSourceFilter(prefix)}
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                    sourceFilter === prefix
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {prefix.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          {/* Model List */}
          <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            {allowEmpty && (
              <button
                type="button"
                onClick={() => setSelected(null)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-200 ${
                  selected === null ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                }`}
              >
                <div className="font-medium text-gray-900">{emptyLabel}</div>
                <div className="text-sm text-gray-500">Disabled</div>
              </button>
            )}

            {groupedModels.map(([family, familyModels]) => (
              <div key={family}>
                {/* Family Header */}
                <div className="sticky top-0 px-4 py-2 bg-gray-100 border-b border-gray-200 font-medium text-sm text-gray-700 uppercase tracking-wide">
                  {family}
                </div>
                {/* Models in this family */}
                {familyModels.map(model => (
                  <button
                    key={model.id}
                    type="button"
                    onClick={() => setSelected(model.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 ${
                      selected === model.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{model.display_name}</span>
                      {model.supports_vision && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          Vision
                        </span>
                      )}
                      {model.supports_reasoning && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Reasoning
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {model.provider || 'Unknown'}
                      {model.max_input_tokens && ` · ${(model.max_input_tokens / 1000).toFixed(0)}K`}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {formatPrice(model) && (
                        <span className="text-xs text-gray-400">
                          {formatPrice(model)} / 1M tokens
                        </span>
                      )}
                      {model.estimated_cost && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                          {language === 'zh'
                            ? `≈¥${(Math.round(model.estimated_cost.estimated_cost_rmb * 20) / 20).toFixed(2)}/轮`
                            : `≈$${(Math.round(model.estimated_cost.estimated_cost_usd * 20) / 20).toFixed(2)}/req`}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ))}

            {groupedModels.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500">
                No models found matching "{search}"
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
